import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface EntityState {
    x: number;
    y: number;
    attachedTo?: string | null;
}

export interface UnifiedKeyframe {
    time: number;
    entities: Record<string, EntityState>;
}

export interface Player {
    id: string;
    shape: 'circle' | 'triangle';
    color: string;
    label: string;
}

export interface AppState {
    players: Player[];
    keyframes: UnifiedKeyframe[];
    currentTime: number;
    isPlaying: boolean;
    duration: number;
    backgroundImageUrl: string | null;
    selectedPlayerId: string | null;
}

const initialState: AppState = {
    players: [],
    keyframes: [{ time: 0, entities: { 'ball': { x: 450, y: 300, attachedTo: null } } }],
    currentTime: 0,
    isPlaying: false,
    duration: 5000,
    backgroundImageUrl: null,
    selectedPlayerId: null,
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        addPlayer: (state, action: PayloadAction<Player & { initialX: number, initialY: number }>) => {
            const { id, shape, color, label, initialX, initialY } = action.payload;
            state.players.push({ id, shape, color, label });
            // Add initial position to current keyframe or create one
            let kf = state.keyframes.find(k => k.time === state.currentTime);
            if (!kf) {
                kf = { time: state.currentTime, entities: {} };
                state.keyframes.push(kf);
                state.keyframes.sort((a, b) => a.time - b.time);
            }
            kf.entities[id] = { x: initialX, y: initialY };
        },
        updatePlayerProps: (state, action: PayloadAction<{ id: string, color?: string, shape?: 'circle' | 'triangle', label?: string }>) => {
            const player = state.players.find(p => p.id === action.payload.id);
            if (player) {
                if (action.payload.color) player.color = action.payload.color;
                if (action.payload.shape) player.shape = action.payload.shape;
                if (action.payload.label) player.label = action.payload.label;
            }
        },
        addKeyframe: (state, action: PayloadAction<{ time: number, entities: Record<string, EntityState> }>) => {
            const { time, entities } = action.payload;
            const existingKfIndex = state.keyframes.findIndex(k => k.time === time);

            if (existingKfIndex >= 0) {
                state.keyframes[existingKfIndex].entities = {
                    ...state.keyframes[existingKfIndex].entities,
                    ...entities
                };
            } else {
                state.keyframes.push({ time, entities });
                state.keyframes.sort((a, b) => a.time - b.time);
            }

            if (time > state.duration) {
                state.duration = time;
            }
        },
        removeKeyframe: (state, action: PayloadAction<number>) => {
            const time = action.payload;
            state.keyframes = state.keyframes.filter(k => k.time !== time);
            if (state.keyframes.length === 0) {
                state.keyframes.push({ time: 0, entities: { 'ball': { x: 450, y: 300, attachedTo: null } } });
            }
        },
        setCurrentTime: (state, action: PayloadAction<number>) => {
            state.currentTime = action.payload;
            if (state.currentTime > state.duration) state.currentTime = state.duration;
            if (state.currentTime < 0) state.currentTime = 0;
        },
        setIsPlaying: (state, action: PayloadAction<boolean>) => {
            state.isPlaying = action.payload;
        },
        setDuration: (state, action: PayloadAction<number>) => {
            state.duration = action.payload;
        },
        setBackgroundImageUrl: (state, action: PayloadAction<string | null>) => {
            state.backgroundImageUrl = action.payload;
        },
        setSelectedPlayerId: (state, action: PayloadAction<string | null>) => {
            state.selectedPlayerId = action.payload;
        }
    }
});

export const {
    addPlayer,
    updatePlayerProps,
    addKeyframe,
    removeKeyframe,
    setCurrentTime,
    setIsPlaying,
    setDuration,
    setBackgroundImageUrl,
    setSelectedPlayerId
} = appSlice.actions;

export const store = configureStore({
    reducer: {
        app: appSlice.reducer
    }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
